import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { LoginShell } from "@/pages/Login";
import { FORGOT_PASSWORD } from "@/constants/testIds/auth";

// Mesmo intervalo de Auth:PasswordResetCooldownMinutes na API: antes disso um novo pedido é ignorado
const RESEND_SECONDS = 120;

// Página "Esqueci a senha" do AdminLTE (pages/examples/forgot-password.html). A API manda por e-mail o mesmo link
// temporário de /definir-senha e responde igual exista ou não o e-mail, então a tela nunca diz se ele tem cadastro
export default function EsqueciSenha() {
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(sp.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const request = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSentTo(email.trim());
      setWait(RESEND_SECONDS);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const mmss = `${Math.floor(wait / 60)}:${String(wait % 60).padStart(2, "0")}`;

  return (
    <LoginShell>
      <div className="card">
        <div className="card-body login-card-body">
          {sentTo ? (
            <div data-testid={FORGOT_PASSWORD.sentMessage}>
              <div className="text-center mb-3">
                <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success elevation-1" style={{ width: 56, height: 56 }}>
                  <i className="fas fa-envelope-open-text fa-lg" />
                </span>
              </div>
              <p className="login-box-msg pb-2">Verifique seu e-mail</p>
              <p className="text-sm text-center">
                Se <b>{sentTo}</b> estiver cadastrado, enviamos um link para definir uma nova senha.
                O link vale por 24 horas e só pode ser usado uma vez.
              </p>
              <div className="callout callout-info text-sm py-2">
                Não chegou? Confira a caixa de spam ou lixo eletrônico e se o e-mail foi digitado certo.
              </div>
              {error && <div className="alert alert-danger py-2 text-sm" role="alert" data-testid={FORGOT_PASSWORD.error}>{error}</div>}
              <button type="button" data-testid={FORGOT_PASSWORD.resendButton} className="btn btn-default btn-block" disabled={wait > 0 || loading} onClick={request}>
                {loading ? <i className="fas fa-spinner fa-spin mr-1" /> : <i className="fas fa-redo mr-1" />}
                {wait > 0 ? `Reenviar em ${mmss}` : "Reenviar e-mail"}
              </button>
              <button type="button" className="btn btn-link btn-block text-sm" onClick={() => { setSentTo(""); setError(""); setWait(0); }}>
                Usar outro e-mail
              </button>
            </div>
          ) : (
            <>
              <p className="login-box-msg">Esqueceu sua senha? Informe seu e-mail e enviaremos um link para você criar uma nova.</p>
              {error && <div className="alert alert-danger py-2" role="alert" data-testid={FORGOT_PASSWORD.error}>{error}</div>}
              <form onSubmit={request} data-testid={FORGOT_PASSWORD.form}>
                <div className="input-group mb-3">
                  <input data-testid={FORGOT_PASSWORD.emailInput} type="email" className="form-control" placeholder="E-mail" autoComplete="username" required autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)} />
                  <div className="input-group-append"><div className="input-group-text"><span className="fas fa-envelope" /></div></div>
                </div>
                <div className="row">
                  <div className="col-12">
                    <button data-testid={FORGOT_PASSWORD.submitButton} type="submit" className="btn btn-primary btn-block" disabled={loading}>
                      {loading && <i className="fas fa-spinner fa-spin mr-1" />}Enviar link de redefinição
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
          <p className="mt-3 mb-1">
            <Link to={`/login${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ""}`} data-testid={FORGOT_PASSWORD.loginLink}>
              <i className="fas fa-arrow-left mr-1" />Voltar para o login
            </Link>
          </p>
        </div>
      </div>
    </LoginShell>
  );
}
